import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import AddToHomeScreenIcon from '@material-ui/icons/AddToHomeScreen';
import CloseIcon from '@material-ui/icons/Close';
const img = require('../../../images/pwa-installation-chrome.png');

// Lightweight, reusable PWA install helper
// - Captures beforeinstallprompt and exposes an Install button
// - Falls back to a help dialog with platform-specific instructions when prompt isn't available
// - Hides itself if already installed (standalone display-mode)

function getPlatformInfo() {
  const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isSamsung = /samsungbrowser/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !/edg\//.test(ua) && !/opr\//.test(ua);
  const isEdge = /edg\//.test(ua);
  const isFirefox = /firefox/.test(ua);
  return { isIOS, isAndroid, isSamsung, isChrome, isEdge, isFirefox };
}

function isStandaloneDisplayMode() {
  const matchStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = typeof window.navigator.standalone === 'boolean' ? window.navigator.standalone : false;
  return Boolean(matchStandalone || iosStandalone);
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(isStandaloneDisplayMode());
  const dismissedRef = useRef(false);
  const deferredPromptRef = useRef(null);
  const DELAY_MS = 3000; // delay before showing fallback CTA
  const AUTO_HIDE_MS = 30000;

  const platform = useMemo(() => getPlatformInfo(), []);
  const isCompactLayout = useMediaQuery('(max-width:600px)');

  // Listen for PWA install events
  useEffect(() => {
    function onBeforeInstallPrompt(e) {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      setDeferredPrompt(e);
      deferredPromptRef.current = e;
      if (!dismissedRef.current) setVisible(true);
    }

    function onAppInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
      setVisible(false);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    // Delay initial banner so that if the beforeinstallprompt arrives quickly, we can show Install directly
    const revealTimer = setTimeout(() => {
      if (!isStandaloneDisplayMode() && !dismissedRef.current) {
        setVisible(true);
      }
    }, DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      clearTimeout(revealTimer);
    };
  }, []);

  // Re-check installed state on visibility changes (e.g., after refresh)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isStandaloneDisplayMode()) {
        setInstalled(true);
        setVisible(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [visible]);

  useEffect(() => {
    if (!visible || installed) return undefined;

    const autoHideTimer = setTimeout(() => {
      dismissedRef.current = true;
      setVisible(false);
    }, AUTO_HIDE_MS);

    return () => clearTimeout(autoHideTimer);
  }, [installed, visible]);

  const promptInstall = useCallback(async () => {
    try {
      if (!deferredPrompt) {
        // No prompt available, show help instead
        setShowHelp(true);
        return;
      }
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // If accepted, we can hide; if dismissed, keep button for later
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } catch (err) {
      // Fallback to help dialog
      setShowHelp(true);
    }
  }, [deferredPrompt]);

  const closeBanner = useCallback(() => {
    dismissedRef.current = true;
    setVisible(false);
  }, []);

  if (installed || !visible) return null;

  const showInstallButton = Boolean(deferredPrompt);
  const buttonLabel = 'Install App';

  return (
    <>
      {/* Floating install banner */}
      <div style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100vw - 32px)',
        zIndex: 1600,
        background: 'rgba(35,47,62,0.97)',
        color: '#fff',
        borderRadius: 12,
        boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
        boxSizing: 'border-box',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: isCompactLayout ? 'column' : 'row',
        alignItems: isCompactLayout ? 'stretch' : 'center',
        gap: 12,
        maxWidth: 440
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0
        }}>
          <div style={{
            width: 40,
            height: 40,
            minWidth: 40,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(97, 175, 254, 0.14)'
          }}>
            <AddToHomeScreenIcon style={{ color: '#61AFFE' }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.25, marginBottom: 2 }}>
              Install ezybill
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.88, lineHeight: 1.45 }}>
              Get faster access with a clean, app-like full-screen experience.
            </Typography>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCompactLayout ? 'space-between' : 'flex-start',
          gap: 6,
          flexShrink: 0,
          width: isCompactLayout ? '100%' : 'auto'
        }}>
          {showInstallButton ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={promptInstall}
              size="medium"
              style={{ width: isCompactLayout ? '100%' : 'auto' }}
            >
              {buttonLabel}
            </Button>
          ) : (
            <Tooltip title="Show how to install on your device">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowHelp(true)}
                size="medium"
                style={{ width: isCompactLayout ? '100%' : 'auto' }}
              >
                How to install
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Hide">
            <IconButton size="small" onClick={closeBanner} style={{ color: '#fff' }} aria-label="Close install banner">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Help instructions dialog */}
      <Dialog open={showHelp} onClose={() => setShowHelp(false)} aria-labelledby="pwa-install-help-title">
        <DialogTitle id="pwa-install-help-title">Install this app</DialogTitle>
        <DialogContent dividers>
          {platform.isAndroid && (
            <div style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">On Android (Chrome)</Typography>
              <Typography variant="body2" color="textSecondary">
                1) Open in Chrome. 2) Tap the ⋮ menu (top right). 3) Choose "Add to Home screen" or "Install app".
              </Typography>
            </div>
          )}
          {platform.isIOS && (
            <div style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">On iPhone/iPad (Safari)</Typography>
              <Typography variant="body2" color="textSecondary">
                1) Open this site in Safari. 2) Tap the Share icon. 3) Select "Add to Home Screen".
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Note: iOS does not support the native install prompt. Use Safari to add to your Home Screen.
              </Typography>
            </div>
          )}
          {!platform.isAndroid && !platform.isIOS && (
            <div style={{ marginBottom: 8 }}>
              <Typography variant="subtitle2">On desktop (Chrome/Edge)</Typography>
              
              <img
                src={(() => {
                  try {
                    return img && img.default ? img.default : img;
                  } catch (err) {
                    // Fallback to CDN if local image is not present
                    return 'https://developer.chrome.com/docs/workbox/images/pwa-installation-chrome.png';
                  }
                })()}
                alt="Install prompt in Chrome"
                style={{ width: '100%', marginTop: 8 }}
              />
              <Typography variant="body2" color="textSecondary">
                Click the Install icon in the address bar, or open the browser menu and choose "Install App".
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {showInstallButton && (
            <Button color="secondary" onClick={promptInstall} variant="contained">
              {buttonLabel}
            </Button>
          )}
          <Button onClick={() => setShowHelp(false)} color="default" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
